import {
  AiUsageByModelRow,
  AiUsageRecordRow,
  AiUsageThreadDetail,
  AiUsageTimelineRow,
  FfmpegUsageTask,
} from "../types/MeGenTypes";

import {
  AiModelRate,
  AiTokenTier,
  PRICE_BOOK,
  PriceBook,
  RateLookup,
  RateOverrides,
  inferUnit,
  priceFor,
  resolveRate,
  unitSuffixOf,
  variantLabelOf,
  variantOf,
} from "./aiModelRates";
import { localDayOf, rateKeyOf } from "./aiPriceBook";
import { formatMetric, formatMoney } from "./aiUsagePivot";
import { canonicalUsageModel, historicalWan26ModelOf, usageModelKeyOf } from "./aiUsageModels";
import { knownTaskCost, knownTaskValue, recordOfFfmpegTask, taskCurrencyOf } from "./ffmpegUsage";

/**
 * Cost engine (pure): usage line × rate → USD
 * กติกา: cloud คิดเฉพาะ call ที่สำเร็จ (ผู้ให้บริการไม่คิดเงินงานที่ fail) — แถว error ยังนับใน calls/errors
 *        gpu_hour (GPU hub) คิดตามเวลาเครื่อง (duration_ms) ทุก call รวม error เพราะเครื่องถูกใช้จริง
 * ทุกฟังก์ชันรับ `book` (default = PRICE_BOOK) เพื่อให้ test ฉีด fixture ได้
 */

/** จำนวนแถว timeline สูงสุดที่ /ai-usage/thread/{id} ส่งกลับ (backend ตัดที่นี่) */
export const TIMELINE_PAGE_SIZE = 200;

export type UsageLine = {
  ffmpeg_task?: FfmpegUsageTask;
  service: string;
  provider: string;
  model: string;
  at?: string | null; // วันที่/เวลา (ISO) ที่ call เกิดขึ้น — เลือกราคาตามช่วงวันที่ (by_model ไม่มี → ใช้ราคาวันนี้)
  resolution?: string | null;
  audio_enabled?: boolean | null;
  status?: string | null; // undefined/null = แถว legacy ที่ถือว่าสำเร็จ
  calls: number;
  images: number;
  video_seconds: number;
  input_video_seconds: number; // R2V: วินาที reference video ที่ผู้ให้บริการบิลเพิ่ม (บิลรวมกับ output ต่อวินาที)
  audio_seconds: number;
  input_tokens: number;
  output_tokens: number;
  process_seconds: number | null; // เวลาเครื่อง (duration_ms/1000) — null = ไม่ทราบ
};

const isBillableStatus = (status?: string | null): boolean => !status || status === "success";

const processSecondsOf = (durationMs: number | null | undefined): number | null =>
  durationMs === null || durationMs === undefined || !Number.isFinite(durationMs) || durationMs <= 0 ? null : durationMs / 1000;

// ─── Adapters (สะพานชื่อ field ที่ต่างกันระหว่าง records / timeline / by_model) ──

const perCallModelFields = (r: {
  service: string; provider: string; model: string; video_duration: number | null;
  resolution?: string | null; audio_enabled?: boolean | null;
}): Pick<UsageLine, "model" | "resolution" | "audio_enabled"> => {
  const historical = historicalWan26ModelOf(r.provider, r.model, r.service === "video" ? r.video_duration : undefined);
  return {
    model: historical || r.model,
    resolution: r.resolution || (historical ? "720P" : r.resolution),
    audio_enabled: r.audio_enabled ?? (historical ? false : r.audio_enabled),
  };
};

export const lineOfRecord = (r: AiUsageRecordRow): UsageLine => ({
  ffmpeg_task: r.ffmpeg_task,
  service: r.service,
  provider: r.provider,
  ...perCallModelFields(r),
  at: r.created_at,
  status: r.status,
  calls: 1,
  images: r.image_count || 0,
  video_seconds: r.video_duration || 0,
  input_video_seconds: r.input_video_seconds || 0,
  audio_seconds: r.audio_duration || 0,
  input_tokens: r.input_tokens || 0,
  output_tokens: r.output_tokens || 0,
  process_seconds: r.ffmpeg_task ? r.ffmpeg_task.allocated_instance_seconds : processSecondsOf(r.duration_ms),
});

export const lineOfTimelineRow = (t: AiUsageTimelineRow): UsageLine => ({
  service: t.service,
  provider: t.provider,
  ...perCallModelFields(t),
  at: t.created_at,
  status: t.status,
  calls: 1,
  images: t.image_count || 0,
  video_seconds: t.video_duration || 0,
  input_video_seconds: t.input_video_seconds || 0,
  audio_seconds: t.audio_duration || 0,
  input_tokens: t.input_tokens || 0,
  output_tokens: t.output_tokens || 0,
  process_seconds: processSecondsOf(t.duration_ms),
});

// Aggregate fallback from /thread. The backend returns one row per pricing variant, including
// status: cloud errors cost $0, while GPU-hour errors still cost their recorded machine time.
const lineOfByModelRow = (m: AiUsageByModelRow): UsageLine => ({
  service: m.service,
  provider: m.provider,
  model: m.model,
  resolution: m.resolution,
  audio_enabled: m.audio_enabled,
  status: m.status,
  calls: m.calls || 0,
  images: m.images || 0,
  video_seconds: m.video_seconds || 0,
  input_video_seconds: m.input_video_seconds || 0,
  audio_seconds: m.audio_seconds || 0,
  input_tokens: m.input_tokens || 0,
  output_tokens: m.output_tokens || 0,
  process_seconds: processSecondsOf(m.duration_ms),
});

// ข้อความปริมาณที่ใช้ — ใช้ร่วมกันทั้ง modal / breakdown / dashboard (field ชื่อเดียวกับ AiUsageAggregate)
export const usageTextOf = (
  u: {
    images: number;
    video_seconds: number;
    input_video_seconds?: number;
    audio_seconds: number;
    input_tokens: number;
    output_tokens: number;
    process_seconds?: number | null;
  },
  includeProcess = false // true = เติมเวลาเครื่อง (แถวที่คิดเงินแบบ gpu_hour)
): string => {
  const parts: string[] = [];
  if (u.images) parts.push(`${formatMetric(u.images)} รูป`);
  if (u.video_seconds) parts.push(`วิดีโอ ${formatMetric(u.video_seconds)} วิ`);
  if (u.input_video_seconds) parts.push(`ref ${formatMetric(u.input_video_seconds)} วิ`);
  if (u.audio_seconds) parts.push(`เสียง ${formatMetric(u.audio_seconds)} วิ`);
  if (u.input_tokens || u.output_tokens)
    parts.push(`tokens ${formatMetric(u.input_tokens || 0)}→${formatMetric(u.output_tokens || 0)}`);
  if (includeProcess && u.process_seconds) parts.push(`เวลาเครื่อง ${formatMetric(u.process_seconds)} วิ`);
  return parts.join(" · ") || "-";
};

// ─── Cost of one line ────────────────────────────────────────────────────────

export type CostResult = {
  knownAllocationCount?: number; // FFmpeg rows with a recorded or calculated USD allocation, including zero.
  calculatedAllocationCount?: number; // FFmpeg rows calculated using the gpuhub|ffmpeg hourly rate.
  knownAllocationTimeCount?: number; // FFmpeg rows with a recorded allocated time, including zero.
  cost: number; // USD (ยังไม่ปัด — แสดงผลผ่าน formatMoney)
  unitPrice: number | null; // ราคาต่อหน่วยที่ใช้ (null = token หรือยังไม่ได้ตั้งราคา)
  variant: string;
  rateLabel: string; // เช่น "$0.15/วิ · 1080P"
  missingRate: boolean; // ยังไม่ได้ตั้งราคา (cost คิดเป็น 0)
  quantityUnknown: boolean; // gpu_hour แต่ไม่ทราบเวลาเครื่อง (cost คิดเป็น 0)
  estimated: boolean; // reasons ไม่ว่าง
  reasons: string[];
  rate: AiModelRate;
};

const round6 = (v: number): number => Math.round(v * 1e6) / 1e6;

// ชั้นราคา token ของ call นี้ (เช่น qwen-plus ≤256K / >256K): หนึ่งแถว usage = หนึ่ง call จึงเลือกได้แม่น
// จาก input_tokens ของแถวเอง — ชั้นแรกที่ขอบบนครอบ input, เกินทุกชั้น = ชั้นสุดท้าย,
// ไม่รู้จำนวน token (0/null) = ไม่เลือกชั้น ใช้ prices บนสุดของ rate
const tokenTierOf = (rate: AiModelRate, inputTokens: number): AiTokenTier | undefined => {
  const tiers = rate.tiers;
  if (!tiers || !tiers.length || !Number.isFinite(inputTokens) || inputTokens <= 0) return undefined;
  return tiers.find((t) => t.maxInputTokens === undefined || inputTokens <= t.maxInputTokens) || tiers[tiers.length - 1];
};

const tokenRangeLabel = (values: number[]): string => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max ? formatMoney(min) : `${formatMoney(min)}–${formatMoney(max)}`;
};

const rateLabelOf = (rate: AiModelRate, variant: string, unitPrice: number | null): string => {
  if (rate.unit === "token_1m") {
    const pin = rate.prices.in;
    const pout = rate.prices.out;
    if (pin === undefined && pout === undefined && !rate.free) return "ยังไม่ได้ตั้งราคา";
    if (rate.tiers?.length) {
      // แสดงเป็นช่วงราคาเพราะแต่ละ call อาจตกคนละชั้น (เช่น "เข้า $0.40–1.20 · ออก $1.60–4.80")
      const ins = [pin, ...rate.tiers.map((t) => t.prices.in)].filter((v): v is number => v !== undefined);
      const outs = [pout, ...rate.tiers.map((t) => t.prices.out)].filter((v): v is number => v !== undefined);
      return `เข้า ${tokenRangeLabel(ins)} · ออก ${tokenRangeLabel(outs)} /1M tokens`;
    }
    return `เข้า ${formatMoney(pin || 0)} · ออก ${formatMoney(pout || 0)} /1M tokens`;
  }
  if (unitPrice === null) return "ยังไม่ได้ตั้งราคา";

  const parts = [`${formatMoney(unitPrice)}${unitSuffixOf(rate.unit)}`];
  const vl = variantLabelOf(variant);
  if (vl) parts.push(vl);
  if (rate.free && unitPrice === 0) parts.push("ฟรี");
  return parts.join(" · ");
};

// ปริมาณที่คิดเงินของ unit=second: tts = เสียง, video = output + reference video ที่ถูกบิลเพิ่ม (r2v)
const secondsOf = (line: UsageLine): number =>
  line.service === "tts" ? line.audio_seconds : line.video_seconds + (line.input_video_seconds || 0);

export const GPU_TIME_UNKNOWN_REASON = "ไม่มีเวลาเครื่อง (duration_ms) ใน log → คิดค่า GPU ไม่ได้";

export const costOfUsage = (line: UsageLine, rate: AiModelRate): CostResult => {
  if (line.ffmpeg_task) {
    const task = line.ffmpeg_task;
    const currency = taskCurrencyOf(task);
    const recordedUsd = knownTaskCost(task) && currency === "USD";
    const knownTime = knownTaskValue(task.allocated_instance_seconds);
    const ffmpegRate = rate.unit === "gpu_hour" && knownTaskValue(rate.prices.hour) ? rate.prices.hour : null;
    // When the task does not store a cost, allocated time × gpuhub|ffmpeg is its actual
    // dashboard cost. Never replace a recorded cost or convert a non-USD allocation.
    const calculatedUsd = !recordedUsd && task.allocated_cost == null && (!currency || currency === "USD")
      && knownTime && ffmpegRate !== null;
    return {
      cost: recordedUsd ? task.allocated_cost! : calculatedUsd ? task.allocated_instance_seconds! * ffmpegRate! / 3600 : 0,
      knownAllocationCount: recordedUsd || calculatedUsd ? 1 : 0,
      calculatedAllocationCount: calculatedUsd ? 1 : 0,
      knownAllocationTimeCount: knownTime ? 1 : 0,
      unitPrice: ffmpegRate,
      variant: "hour",
      rateLabel: rateLabelOf(rate, "hour", ffmpegRate),
      missingRate: !recordedUsd && !calculatedUsd,
      quantityUnknown: !knownTime,
      estimated: false,
      reasons: [],
      rate,
    };
  }
  const { variant, reasons } = variantOf(rate, line);
  const isGpu = rate.unit === "gpu_hour";
  const billable = isGpu || isBillableStatus(line.status); // GPU hub: เวลาเครื่องถูกใช้แม้ job fail
  let cost = 0;
  let unitPrice: number | null = null;
  let missingRate = false;
  let quantityUnknown = false;

  if (rate.unit === "token_1m") {
    // ชั้นราคา (เช่น qwen-plus ≤256K ถูกกว่า >256K): Alibaba เลือกชั้นตาม input tokens ของ call นั้น ๆ
    const tier = tokenTierOf(rate, line.input_tokens);
    const pin = tier ? tier.prices.in : rate.prices.in;
    const pout = tier ? tier.prices.out : rate.prices.out;
    missingRate = !rate.free && (pin === undefined || pout === undefined);
    if (billable) cost = (line.input_tokens * (pin || 0) + line.output_tokens * (pout || 0)) / 1e6;
  } else {
    const { price, reason } = priceFor(rate, variant);
    if (reason) reasons.push(reason);
    missingRate = price === undefined;
    unitPrice = price === undefined ? null : price;

    let qty: number;
    if (rate.unit === "image") qty = line.images;
    else if (rate.unit === "call") qty = line.calls;
    else if (isGpu) {
      if (line.process_seconds === null || line.process_seconds === undefined) {
        quantityUnknown = line.calls > 0;
        if (quantityUnknown) reasons.push(GPU_TIME_UNKNOWN_REASON);
        qty = 0;
      } else {
        qty = line.process_seconds / 3600;
      }
    } else qty = secondsOf(line);

    if (billable) cost = qty * (price || 0);
  }

  return {
    cost, // ไม่ปัดที่นี่ — ปัดตอนรวมแถว/ยอดรวม (ปัดต่อ call แล้วบวกจะเพี้ยน เช่น 3 × 0.015333)
    unitPrice,
    variant,
    rateLabel: rateLabelOf(rate, variant, unitPrice),
    missingRate,
    quantityUnknown,
    estimated: reasons.length > 0,
    reasons,
    rate,
  };
};

// รวม flags/reasons ของหลาย call เข้าแถวเดียว (OR / union) — ห้ามใช้ค่าจาก call แรกอย่างเดียว
const mergeResult = (acc: CostResult, single: CostResult) => {
  acc.cost += single.cost;
  if (single.knownAllocationCount !== undefined) acc.knownAllocationCount = (acc.knownAllocationCount || 0) + single.knownAllocationCount;
  if (single.calculatedAllocationCount !== undefined) acc.calculatedAllocationCount = (acc.calculatedAllocationCount || 0) + single.calculatedAllocationCount;
  if (single.knownAllocationTimeCount !== undefined) acc.knownAllocationTimeCount = (acc.knownAllocationTimeCount || 0) + single.knownAllocationTimeCount;
  acc.missingRate = acc.missingRate || single.missingRate;
  acc.quantityUnknown = acc.quantityUnknown || single.quantityUnknown;
  single.reasons.forEach((r) => {
    if (!acc.reasons.includes(r)) acc.reasons.push(r);
  });
  acc.estimated = acc.reasons.length > 0;
};

const emptyResultLike = (single: CostResult): CostResult => ({
  ...single,
  knownAllocationCount: single.knownAllocationCount === undefined ? undefined : 0,
  calculatedAllocationCount: single.calculatedAllocationCount === undefined ? undefined : 0,
  knownAllocationTimeCount: single.knownAllocationTimeCount === undefined ? undefined : 0,
  cost: 0,
  reasons: [],
  missingRate: false,
  quantityUnknown: false,
  estimated: false,
});

// ─── Run modal rows (model × variant) ────────────────────────────────────────

export type RunCostRow = {
  key: string; // `${provider}|${model}|${variant}`
  service: string;
  provider: string;
  model: string;
  variant: string;
  calls: number;
  errors: number;
  usage: UsageLine; // ปริมาณรวมของแถว (process_seconds = ผลรวมเท่าที่ทราบ)
  result: CostResult; // cost = รวมทั้งแถว, unitPrice/rateLabel/rate = ราคาที่ใช้
  fallbackFrom?: string; // ยืมราคาจาก (snapshot / ค่าเริ่มต้นของ provider)
};

export type RunCostSummary = {
  rows: RunCostRow[];
  total: number;
  complete: boolean; // timeline ครอบคลุมทุก call (backend ตัดที่ TIMELINE_PAGE_SIZE)
  missingCount: number; // แถวที่ยังไม่ได้ตั้งราคา
  unknownQuantityCount: number; // แถว gpu_hour ที่ไม่ทราบเวลาเครื่อง
  estimated: boolean;
};

const resolveUsageRate = (line: UsageLine, overrides: RateOverrides, book: PriceBook): RateLookup =>
  resolveRate(line, overrides, book);

export const buildRunCostRows = (
  detail: AiUsageThreadDetail,
  overrides: RateOverrides,
  book: PriceBook = PRICE_BOOK
): RunCostSummary => {
  const timeline = detail.timeline || [];
  const totalCalls = detail.totals?.calls || 0;
  const complete = timeline.length >= totalCalls;
  const groups = new Map<string, RunCostRow>();

  const add = (line: UsageLine, errors: number, extraReason?: string) => {
    const { rate, fallbackFrom } = resolveUsageRate(line, overrides, book);
    const single = costOfUsage(line, rate);
    if (extraReason) single.reasons.push(extraReason);

    const model = canonicalUsageModel(line.provider, line.model);
    const key = `${line.provider}|${model}|${single.variant}`;
    let row = groups.get(key);

    if (!row) {
      row = {
        key,
        service: line.service,
        provider: line.provider,
        model,
        variant: single.variant,
        calls: 0,
        errors: 0,
        usage: {
          ...line,
          status: undefined,
          calls: 0,
          images: 0,
          video_seconds: 0,
          input_video_seconds: 0,
          audio_seconds: 0,
          input_tokens: 0,
          output_tokens: 0,
          process_seconds: 0,
        },
        result: emptyResultLike(single),
        fallbackFrom,
      };
      groups.set(key, row);
    }

    row.calls += line.calls;
    row.errors += errors;
    row.usage.calls += line.calls;
    row.usage.images += line.images;
    row.usage.video_seconds += line.video_seconds;
    row.usage.input_video_seconds += line.input_video_seconds;
    row.usage.audio_seconds += line.audio_seconds;
    row.usage.input_tokens += line.input_tokens;
    row.usage.output_tokens += line.output_tokens;
    row.usage.process_seconds = (row.usage.process_seconds || 0) + (line.process_seconds || 0);
    mergeResult(row.result, single);
  };

  if (complete) {
    timeline.forEach((t) => {
      const line = lineOfTimelineRow(t);
      add(line, isBillableStatus(line.status) ? 0 : 1);
    });
  } else {
    (detail.by_model || []).forEach((m) => add(lineOfByModelRow(m), m.errors || 0));
  }
  (detail.ffmpeg_tasks || []).forEach((task) => add(lineOfRecord(recordOfFfmpegTask(task)), task.status === "error" ? 1 : 0));

  const rows = Array.from(groups.values())
    .map((r) => ({ ...r, result: { ...r.result, cost: round6(r.result.cost) } }))
    .sort((a, b) => b.result.cost - a.result.cost || b.calls - a.calls);

  return {
    rows,
    total: round6(rows.reduce((sum, r) => sum + r.result.cost, 0)),
    complete,
    missingCount: rows.filter((r) => r.service !== "ffmpeg" && r.result.missingRate).length,
    unknownQuantityCount: rows.filter((r) => r.service !== "ffmpeg" && r.result.quantityUnknown).length,
    estimated: rows.some((r) => r.result.estimated),
  };
};

// ─── Dashboard helpers (raw records) ─────────────────────────────────────────

export type RecordCostInfo = { result: CostResult; fallbackFrom?: string };

// cache rate ต่อ provider|model|unit-ที่เดา|วันที่ (rate ที่ inferred ขึ้นกับ usage ของ record → ต้องแยกตาม unit;
// model ที่มีหลายช่วงราคา → ต้องแยกตามวันด้วย ไม่งั้นทุกแถวจะได้ราคาของแถวแรก)
const makeRateResolver = (overrides: RateOverrides, book: PriceBook) => {
  const cache = new Map<string, RateLookup>();
  return (line: UsageLine): RateLookup => {
    const k = `${rateKeyOf(line.provider, line.model)}|${inferUnit(line)}|${localDayOf(line.at) || ""}`;
    let lookup = cache.get(k);
    if (!lookup) {
      lookup = resolveUsageRate(line, overrides, book);
      cache.set(k, lookup);
    }
    return lookup;
  };
};

// Cached per-record details for paged tables and totals, without rewriting raw log models.
export const makeRecordCostInfoFn = (overrides: RateOverrides, book: PriceBook = PRICE_BOOK) => {
  const resolve = makeRateResolver(overrides, book);
  return (r: AiUsageRecordRow): RecordCostInfo => {
    const line = lineOfRecord(r);
    const lookup = resolve(line);
    return { result: costOfUsage(line, lookup.rate), fallbackFrom: lookup.fallbackFrom };
  };
};

// costFn สำหรับ aggregateRecords (records ทุกหน้าที่โหลดครบแล้ว)
export const makeRecordCostFn = (overrides: RateOverrides, book: PriceBook = PRICE_BOOK) => {
  const resolve = makeRateResolver(overrides, book);
  return (r: AiUsageRecordRow): number => {
    const line = lineOfRecord(r);
    return costOfUsage(line, resolve(line).rate).cost;
  };
};

// key ของ "price line" (model × variant) — ไม่ขึ้นกับ override เพื่อให้แถวในตารางไม่สลับตอนแก้ราคา
export const priceLineKeyOf = (r: AiUsageRecordRow, book: PriceBook = PRICE_BOOK): string => {
  if (r.ffmpeg_task) return `${usageModelKeyOf(r)}|hour`;
  const line = lineOfRecord(r);
  const { rate } = resolveRate(line, {}, book);
  const { variant } = variantOf(rate, line);
  return `${usageModelKeyOf(line)}|${variant}`;
};

export type PriceLineInfo = RecordCostInfo & { calls: number; unknownQuantityCalls: number };

// ข้อมูลราคาต่อ price line (key เดียวกับ priceLineKeyOf) — flags/reasons รวมทุก record ของบรรทัด ไม่ใช่แค่ record แรก
export const buildPriceLineInfos = (
  records: AiUsageRecordRow[],
  overrides: RateOverrides,
  book: PriceBook = PRICE_BOOK
): Map<string, PriceLineInfo> => {
  const resolve = makeRateResolver(overrides, book);
  const out = new Map<string, PriceLineInfo>();

  records.forEach((r) => {
    const line = lineOfRecord(r);
    const lookup = resolve(line);
    const single = costOfUsage(line, lookup.rate);
    const key = `${usageModelKeyOf(line)}|${single.variant}`;

    let info = out.get(key);
    if (!info) {
      info = { result: emptyResultLike(single), fallbackFrom: lookup.fallbackFrom, calls: 0, unknownQuantityCalls: 0 };
      out.set(key, info);
    }
    info.calls += 1;
    if (single.quantityUnknown) info.unknownQuantityCalls += 1;
    mergeResult(info.result, single);
  });

  return out;
};
